import { ToastManagerContext } from 'components/ToastManager';
import {
  type CreateApiClientMutation,
  useCreateApiClientMutation,
  WorkspaceRoleKey,
} from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useCallback, useState } from 'react';
import { getResultOrError } from 'utils/errors';
import { workspaceRoleOptions } from 'utils/workspaceRoles';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { apiClientFieldLabelClassName } from '../apiClientFieldStyles';

export type ApiClient = CreateApiClientMutation['createApiClient'] & {
  __typename: 'CreateApiClientResult';
};

type Props = {
  onCreate: (result: ApiClient) => void;
  onCancel: VoidFunction;
};

export const ApiClientForm = ({ onCreate, onCancel }: Props) => {
  const { showToast } = useContextSafely(ToastManagerContext);
  const { context } = useInternalApiContext();
  const [{ fetching }, createClient] = useCreateApiClientMutation();
  const [clientName, setName] = useState('');
  const [role, setRole] = useState(WorkspaceRoleKey.WorkspaceAdmin);

  const workspaceId = useWorkspaceId();
  const handleCreate = useCallback(async () => {
    const { data, error } = await createClient(
      {
        workspaceId,
        clientName,
        role,
      },
      context
    );
    const res = getResultOrError(data?.createApiClient, error);

    if (res.isError) {
      showToast(<Toast message="Error Creating API Key" type="error" />);
    } else {
      onCreate(res.result);
    }
  }, [
    createClient,
    workspaceId,
    clientName,
    role,
    context,
    onCreate,
    showToast,
  ]);

  return (
    <form
      className="flex flex-col space-y-f2 h-full w-full"
      onSubmit={(e) => {
        e.preventDefault();
        handleCreate().catch(() =>
          showToast(<Toast message="Error Creating API Key" type="error" />)
        );
      }}
    >
      <TextInput
        flexContainer="space-y-f0"
        id="new-client-name"
        label="Name"
        labelClassName={apiClientFieldLabelClassName}
        placeholder="client-name"
        size="inherit"
        value={clientName}
        onChange={(e) => setName(e.target.value)}
        padding="py-f0 px-0"
        width="w-full"
      />
      <Dropdown
        label="Role"
        labelClassName={apiClientFieldLabelClassName}
        displayValue={role}
        onChange={(value) => setRole(value as WorkspaceRoleKey)}
        options={workspaceRoleOptions}
        size="inherit"
        width="w-full"
        border
        data-testid="api-client-create-role-dropdown"
      />
      <FormButtons
        type="tertiary"
        onCancel={onCancel}
        cancelButtonLabel="Cancel"
        submitButtonLabel={fetching ? 'Creating...' : 'Create'}
        disabled={!clientName.trim()}
      />
    </form>
  );
};
